// Phase 85.A.121 — Rating Video Proposal gate.
// v17.perf.har2 (02/2026) — Migrate sang React Query để share cache, giảm
// double-fetch StrictMode/route change. Polling giữ qua `refetchInterval`.
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";
import RatingVideoProposalRequiredModal from "../components/athlete/RatingVideoProposalRequiredModal";
import RatingVideoUploadModal from "../components/athlete/personal/RatingVideoUploadModal";
import { api } from "../lib/api";
import { isUploadActive } from "../lib/uploadActivity";

const ESCAPE_PATHS = ["/login", "/register", "/auth/callback", "/logout", "/403"];
const POLL_INTERVAL_MS = 60_000;

export const RATING_VIDEO_PROPOSALS_QUERY_KEY = ["athlete", "rating-video-proposals"];

export function RatingVideoProposalGateProvider({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const qc = useQueryClient();
  // Phase 85.A.145 — Upload modal state ở cấp context để KHÔNG bị unmount
  // khi activeProposal chuyển null sau accept.
  const [uploadOpen, setUploadOpen] = useState(false);

  const onEscapeRoute = ESCAPE_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
  );

  // Đợi PhoneVerify gate clear trước.
  const phoneGateActive =
    !loading && !!user && user.auth_provider === "google" && user.is_phone_verified === false;

  const shouldFetch = !loading && !!user && !onEscapeRoute && !phoneGateActive;

  const { data: items, refetch } = useQuery({
    queryKey: RATING_VIDEO_PROPOSALS_QUERY_KEY,
    enabled: shouldFetch,
    queryFn: async () => {
      // Phase 85.A.150 — Skip khi user đang upload (tránh remount cascade).
      if (uploadOpen || isUploadActive()) return [];
      try {
        const r = await api.get("/athlete/rating-video-proposals");
        return Array.isArray(r.data) ? r.data : [];
      } catch {
        return [];
      }
    },
    staleTime: Infinity,
    refetchInterval: shouldFetch && !uploadOpen ? POLL_INTERVAL_MS : false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  // v17.perf.har3 (13/08/2026) — Xoá custom `focus` handler gọi `refetch()`
  // (regression trong v17.perf.har2 khiến rating-video-proposals tăng 3x→7x).
  useEffect(() => {
    if (!shouldFetch) return undefined;
    const onCustom = () => refetch();
    window.addEventListener("rating-video-proposal-changed", onCustom);
    return () => {
      window.removeEventListener("rating-video-proposal-changed", onCustom);
    };
  }, [shouldFetch, refetch]);

  const activeProposal = Array.isArray(items) && items.length ? items[0] : null;

  const handleAccepted = () => {
    setUploadOpen(true);
  };

  const handleResolved = () => {
    // Optimistic drop proposal đã resolve khỏi cache; refetch sau 1.5s.
    if (activeProposal?.id) {
      qc.setQueryData(RATING_VIDEO_PROPOSALS_QUERY_KEY, (prev) =>
        Array.isArray(prev) ? prev.filter((p) => p.id !== activeProposal.id) : [],
      );
    }
    setTimeout(() => refetch(), 1500);
  };

  const handleUploadClose = () => {
    setUploadOpen(false);
    handleResolved();
  };

  return (
    <>
      {children}
      {shouldFetch && activeProposal && !uploadOpen ? (
        <RatingVideoProposalRequiredModal
          proposal={activeProposal}
          onAccepted={handleAccepted}
          onResolved={handleResolved}
        />
      ) : null}
      {/* Phase 85.A.145 — Render độc lập, không bị unmount khi proposal=null. */}
      <RatingVideoUploadModal
        open={uploadOpen}
        onClose={handleUploadClose}
        onSubmitted={handleUploadClose}
        variant="proposal_required"
      />
    </>
  );
}
